/**
 * @file Photo pricings utils.
 */

/**
 * @typedef {Object} PhotoMinPricingError
 * @property {Array|boolean} errors - The errors array.
 */

import { PRINT_ASSET_TYPES } from '../constants/print-assets';
import { filterPrintAssetsByJerseyColors } from './print-assets';

/**
 * Validates that print package print items' minimum prices
 * have been properly set, returning error array if not.
 *
 * @param {Array<Object>} printItems - The selected print items.
 * @returns {PhotoMinPricingError} - Object containing errors.
 */
const validatePhotoPricingMinPrices = (printItems = []) => {
  if (!printItems || !printItems.length) {
    return { errors: false };
  }

  const errors = printItems.reduce((acc, printItem) => {
    if (
      printItem.price <
      (printItem.item_type?.min_price?.value ??
        printItem?.print_size?.min_price?.value ??
        printItem?.print_size?.price?.value)
    ) {
      acc.push(
        printItem.item_type ?? {
          print_product: printItem.print_product,
          print_product_size: printItem.print_size,
        },
      );
    }

    return acc;
  }, []);

  return {
    errors: errors.length > 0 ? errors : false,
  };
};

/**
 * Returns a list of print backgrounds for a given photo pricing
 *
 * The gallery's own backdrops take precedence, but only on a green screen
 * gallery — same reasoning as `getPrintPackagePrintBackgrounds`.
 *
 * @param {Object} photoPricing The Album Photo pricing to get print backgrounds from.
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup to get print backgrounds from.
 * @param {Array} jerseyColors the jersey colors used by the active identity
 * @returns {*[]}
 */
const getPhotoPricingPrintBackgrounds = (
  photoPricing,
  albumMembershipGroup,
  jerseyColors = [],
) => {
  const albumFolder =
    albumMembershipGroup?.album?.root_album_folder ??
    albumMembershipGroup?.album_folder;

  if (
    albumFolder?.green_screen_enabled &&
    albumFolder?.print_assets?.records?.length > 0
  ) {
    const assets = (albumFolder?.print_assets?.records ?? [])
      .filter(
        ({ print_asset: printAsset }) =>
          printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
      )
      .map(printPackagePrintAsset => printPackagePrintAsset.print_asset);

    return filterPrintAssetsByJerseyColors(assets, jerseyColors);
  }

  if (!photoPricing) {
    return [];
  }

  const assets = (photoPricing?.print_assets?.records ?? [])
    .filter(
      ({ print_asset: printAsset }) =>
        printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
    )
    .map(printPackagePrintAsset => printPackagePrintAsset.print_asset);

  return filterPrintAssetsByJerseyColors(assets, jerseyColors);
};

/**
 * Checks if a photo pricing contains ROES-based items
 *
 * @param {Object} photoPricing - The photo pricing object
 * @returns {boolean} - True if the pricing has ROES-based items
 */
const photoPricingHasRoesBasedItems = photoPricing => {
  const items = photoPricing?.print_product_items?.records ?? [];
  return items.some(
    item =>
      !!item.print_product_configuration?.print_product_size_roes_graphic_node,
  );
};

/**
 * Sums what a set of individual product items comes to.
 *
 * @param {Array<Object>} items - Items shaped as `{ price }`.
 * @returns {number}
 */
const getPhotoPricingTotal = (items = []) =>
  items.reduce((total, item) => total + (item.price ?? 0), 0);

/**
 * Flattens a saved photo pricing's items into the shape the pricing overview
 * and the product details list expect.
 *
 * @param {Object} photoPricing - The saved photo pricing record.
 * @returns {Array<{id: string, name: string, price: number}>}
 */
const getPhotoPricingItems = photoPricing => {
  const printProductItems = (
    photoPricing?.print_product_items?.records ?? []
  ).map(item => ({
    id: item.id,
    name: `${item?.print_product?.name ?? ''}${
      item?.print_product_size?.print_size?.name
        ? ` - ${item.print_product_size.print_size.name}`
        : ''
    }`,
    price: item?.price?.value ?? 0,
  }));

  const printItems = (photoPricing?.print_items?.records ?? []).map(item => ({
    id: item.id,
    name: item?.type?.name ?? '',
    price: item?.price?.value ?? 0,
  }));

  return [...printProductItems, ...printItems];
};

/**
 * Flattens the items the photo pricing form is holding in state into the shape
 * the selected products summary and the pricing overview expect.
 *
 * @param {Object} photoPricingData - The form's working state.
 * @returns {Array<{id: string, name: string, price: number}>}
 */
const getPhotoPricingFormItems = photoPricingData => {
  const printProductItems = (photoPricingData?.printProductItems ?? []).map(
    item => ({
      id: item.id,
      name: `${item?.print_product?.name ?? ''}${
        item?.print_size?.print_size?.name
          ? ` - ${item.print_size.print_size.name}`
          : ''
      }`,
      price: item.price ?? 0,
    }),
  );

  const printTypeItems = (photoPricingData?.printTypeItems ?? []).map(item => ({
    id: item.id,
    name: item?.item_type?.name ?? '',
    price: item.price ?? 0,
  }));

  return [...printProductItems, ...printTypeItems];
};

export {
  validatePhotoPricingMinPrices,
  getPhotoPricingPrintBackgrounds,
  photoPricingHasRoesBasedItems,
  getPhotoPricingTotal,
  getPhotoPricingItems,
  getPhotoPricingFormItems,
};
